import { useCallback, useEffect, useState } from "react";

/** Folders for Calls. The API does not store them; this browser does. */
const KEY = "dooglie.calls.folders";
const MAX_FOLDERS = 100;
const MAX_NAME = 80;

export type CallFolder = {
  id: string;
  name: string;
  callIds: string[];
  /** Null when the folder sits on the main list. */
  parentId: string | null;
  /** When the folder was created. Sort uses this for recently added. */
  createdAt: string;
};

export function cleanFolderName(name: string): string {
  return name.trim().replace(/\s+/g, " ").slice(0, MAX_NAME);
}

/** True when two labels are the same folder or meeting name. */
export function namesMatch(left: string, right: string): boolean {
  const a = cleanFolderName(left).toLocaleLowerCase();
  const b = cleanFolderName(right).toLocaleLowerCase();
  return a.length > 0 && a === b;
}

export function folderNameTaken(folders: CallFolder[], name: string, exceptId?: string): boolean {
  return folders.some((folder) => folder.id !== exceptId && namesMatch(folder.name, name));
}

function folderId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `folder-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function loadCallFolders(): CallFolder[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const folders: CallFolder[] = [];
    for (const item of parsed) {
      if (!item || typeof item !== "object") continue;
      const row = item as {
        id?: unknown;
        name?: unknown;
        callIds?: unknown;
        parentId?: unknown;
        createdAt?: unknown;
      };
      if (typeof row.id !== "string" || !row.id || typeof row.name !== "string") continue;
      const name = cleanFolderName(row.name);
      if (!name) continue;
      const callIds = Array.isArray(row.callIds)
        ? [
            ...new Set(
              row.callIds.filter((id): id is string => typeof id === "string" && id.length > 0),
            ),
          ]
        : [];
      const parentId = typeof row.parentId === "string" && row.parentId ? row.parentId : null;
      const createdAt =
        typeof row.createdAt === "string" && !Number.isNaN(Date.parse(row.createdAt))
          ? row.createdAt
          : "";
      folders.push({ id: row.id, name, callIds, parentId, createdAt });
      if (folders.length >= MAX_FOLDERS) break;
    }
    const stamped = folders.map((folder, index) =>
      folder.createdAt
        ? folder
        : { ...folder, createdAt: new Date(Date.now() - index * 1000).toISOString() },
    );
    const ids = new Set(stamped.map((folder) => folder.id));
    return stamped.map((folder) =>
      folder.parentId && !ids.has(folder.parentId) ? { ...folder, parentId: null } : folder,
    );
  } catch {
    return [];
  }
}

export function saveCallFolders(folders: CallFolder[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(folders.slice(0, MAX_FOLDERS)));
  } catch {
    // A full or private store still leaves the list usable.
  }
}

export function createFolder(
  folders: CallFolder[],
  name: string,
  parentId: string | null = null,
): CallFolder[] {
  const cleaned = cleanFolderName(name);
  const parent = parentId && folders.some((folder) => folder.id === parentId) ? parentId : null;
  if (!cleaned || folders.length >= MAX_FOLDERS || folderNameTaken(folders, cleaned))
    return folders;
  return [
    {
      id: folderId(),
      name: cleaned,
      callIds: [],
      parentId: parent,
      createdAt: new Date().toISOString(),
    },
    ...folders,
  ];
}

export function renameFolder(folders: CallFolder[], id: string, name: string): CallFolder[] {
  const cleaned = cleanFolderName(name);
  if (!cleaned || folderNameTaken(folders, cleaned, id)) return folders;
  return folders.map((folder) => (folder.id === id ? { ...folder, name: cleaned } : folder));
}

/** This folder and every folder inside it. */
export function folderTreeIds(folders: CallFolder[], id: string): string[] {
  const ids: string[] = [];
  const walk = (current: string) => {
    if (ids.includes(current)) return;
    ids.push(current);
    for (const folder of folders) {
      if (folder.parentId === current) walk(folder.id);
    }
  };
  if (folders.some((folder) => folder.id === id)) walk(id);
  return ids;
}

/** True when `nodeId` sits inside `ancestorId`. */
export function folderContains(folders: CallFolder[], ancestorId: string, nodeId: string): boolean {
  let current = folders.find((folder) => folder.id === nodeId);
  const seen = new Set<string>();
  while (current?.parentId) {
    if (seen.has(current.id)) return false;
    seen.add(current.id);
    if (current.parentId === ancestorId) return true;
    const parentId = current.parentId;
    current = folders.find((folder) => folder.id === parentId);
  }
  return false;
}

export function meetingsInTree(folders: CallFolder[], id: string): string[] {
  const tree = new Set(folderTreeIds(folders, id));
  const calls = new Set<string>();
  for (const folder of folders) {
    if (!tree.has(folder.id)) continue;
    for (const callId of folder.callIds) calls.add(callId);
  }
  return [...calls];
}

export function deleteFolder(folders: CallFolder[], id: string): CallFolder[] {
  const gone = new Set(folderTreeIds(folders, id));
  return folders.filter((folder) => !gone.has(folder.id));
}

/** Nest a folder, unless that would put it inside itself. */
export function placeFolderInFolder(
  folders: CallFolder[],
  folderId: string,
  parentId: string | null,
): CallFolder[] {
  if (!folders.some((folder) => folder.id === folderId)) return folders;
  if (folderId === parentId) return folders;
  if (parentId && !folders.some((folder) => folder.id === parentId)) return folders;
  if (parentId && folderContains(folders, folderId, parentId)) return folders;
  return folders.map((folder) => (folder.id === folderId ? { ...folder, parentId } : folder));
}

export function toggleCallFolder(
  folders: CallFolder[],
  folderId: string,
  botId: string,
): CallFolder[] {
  if (!botId) return folders;
  return folders.map((folder) => {
    if (folder.id !== folderId) return folder;
    const has = folder.callIds.includes(botId);
    return {
      ...folder,
      callIds: has ? folder.callIds.filter((id) => id !== botId) : [...folder.callIds, botId],
    };
  });
}

/** Move a meeting into this folder and out of every other one. */
export function placeCallInFolder(
  folders: CallFolder[],
  folderId: string,
  botId: string,
): CallFolder[] {
  if (!botId || !folders.some((folder) => folder.id === folderId)) return folders;
  return folders.map((folder) => {
    const without = folder.callIds.filter((id) => id !== botId);
    return folder.id === folderId
      ? { ...folder, callIds: [...without, botId] }
      : { ...folder, callIds: without };
  });
}

/** File the meeting in this folder, or put it back on the main list if it is already there. */
export function fileCall(folders: CallFolder[], folderId: string, botId: string): CallFolder[] {
  const target = folders.find((folder) => folder.id === folderId);
  if (!target || !botId) return folders;
  if (target.callIds.includes(botId)) return toggleCallFolder(folders, folderId, botId);
  return placeCallInFolder(folders, folderId, botId);
}

export function forgetCallFolders(folders: CallFolder[], botId: string): CallFolder[] {
  return folders.map((folder) => ({
    ...folder,
    callIds: folder.callIds.filter((id) => id !== botId),
  }));
}

/** Folder names a call belongs to, for search and the line under the title. */
export function folderNamesFor(botId: string, folders: CallFolder[]): string[] {
  return folders.filter((folder) => folder.callIds.includes(botId)).map((folder) => folder.name);
}

export function useCallFolders() {
  const [folders, setFolders] = useState(loadCallFolders);

  const update = useCallback((recipe: (current: CallFolder[]) => CallFolder[]) => {
    setFolders((current) => {
      const next = recipe(current);
      if (next !== current) saveCallFolders(next);
      return next;
    });
  }, []);

  useEffect(() => {
    const refresh = () => setFolders(loadCallFolders());
    const onStorage = (event: StorageEvent) => {
      if (event.key === KEY || event.key === null) refresh();
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  return { folders, update };
}
